import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import WelcomeTour from '@/components/feature/WelcomeTour';
import { acessoDaConta } from '@/lib/acessoDaConta';
import { useAuth } from '@/hooks/useAuth';

const ROTA_PLANO = '/gestao-plano/plano';

interface Verificacao {
  usuario: string | null;
  caminho: string;
  liberado: boolean;
  /** Fim da liberação provisória (P-46), em ms; `null` quando vem da assinatura. */
  ate: number | null;
}

export default function PlanGuard() {
  const { user, isSuperuser, loading: authLoading } = useAuth();
  const location = useLocation();
  const [verificacao, setVerificacao] = useState<Verificacao | null>(null);

  const usuario = user?.id ?? null;
  const caminho = location.pathname;

  // Liberado, fica liberado enquanto o usuario for o mesmo. Bloqueado, vale so
  // para o caminho em que foi verificado: cada navegacao de quem esta bloqueado
  // consulta de novo.
  //
  // Antes a verificacao rodava uma vez por montagem — e esta rota de layout nao
  // desmonta ao trocar de tela. Quem pagava um boleto com a aplicacao aberta
  // ficava preso em `/gestao-plano/plano` mesmo depois de o webhook (ou o
  // `get-subscription`, que sincroniza com o Stripe ao abrir aquela tela) marcar
  // a assinatura como ativa: so um F5 liberava.
  const verificacaoAtual =
    verificacao !== null &&
    verificacao.usuario === usuario &&
    (verificacao.liberado || verificacao.caminho === caminho);

  useEffect(() => {
    if (authLoading || verificacaoAtual) return;

    if (isSuperuser || !user) {
      setVerificacao({ usuario, caminho, liberado: isSuperuser, ate: null });
      return;
    }

    let ativo = true;

    acessoDaConta(user.id)
      .then(({ liberado, ate }) => {
        if (ativo) setVerificacao({ usuario, caminho, liberado, ate });
      })
      .catch(() => {
        if (ativo) setVerificacao({ usuario, caminho, liberado: false, ate: null });
      });

    return () => {
      ativo = false;
    };
  }, [user, usuario, isSuperuser, authLoading, caminho, verificacaoAtual]);

  // Liberação provisória tem prazo: quando ele passa, descarta a verificação e
  // o efeito acima consulta de novo — sem esperar a próxima navegação. Se o
  // boleto compensou nesse meio-tempo, a assinatura já está ativa e nada muda.
  useEffect(() => {
    if (!verificacao?.liberado || verificacao.ate === null) return;
    const restante = Math.max(0, verificacao.ate - Date.now());
    const timer = setTimeout(() => setVerificacao(null), restante);
    return () => clearTimeout(timer);
  }, [verificacao]);

  const hasPlan = verificacaoAtual && verificacao.liberado;

  if (authLoading || !verificacaoAtual) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500 mt-3">Verificando assinatura...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasPlan && !isSuperuser && caminho !== ROTA_PLANO) {
    return <Navigate to={ROTA_PLANO} replace />;
  }

  // O tour mora aqui, e nao no `DashboardLayout`.
  //
  // Cada tela protegida monta o proprio `DashboardLayout`, entao navegar
  // desmonta o layout da tela que sai e monta o da que entra. Com o tour la
  // dentro, cada passo que navegava remontava o componente e zerava o estado:
  // a pessoa clicava em "Proximo: Dashboard", a tela trocava, e o tour voltava
  // para o passo 1 — parecendo travado no mesmo lugar para sempre.
  //
  // Esta e a rota de layout do bloco protegido: ela permanece montada enquanto
  // as filhas trocam, que e exatamente o tempo de vida que um tour entre telas
  // precisa ter.
  return (
    <>
      <Outlet />
      <WelcomeTour />
    </>
  );
}
